import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { motion } from 'motion/react'
import { CheckCircle2, Eye, EyeOff, Loader2 } from 'lucide-react'
import { useAuth } from './AuthContext'

export default function SetPassword() {
  const { t } = useTranslation('auth')
  const { updatePassword, clearPasswordSet } = useAuth()
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (password !== confirmPassword) {
      setError(t('passwordMismatch'))
      return
    }

    setLoading(true)
    const { error: updateError } = await updatePassword(password)
    setLoading(false)
    if (updateError) {
      setError(updateError)
    } else {
      setSuccess(true)
    }
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-full px-8 pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-sm"
      >
        <div className="text-center mb-10">
          <img src="/tendbot.png" alt="TendBot" className="h-20 w-auto mx-auto mb-3" />
          <h1 className="font-display text-4xl text-gold font-semibold mb-2">TendBot</h1>
          <p className="text-pearl/50 text-sm">{t('tagline')}</p>
        </div>

        <div className="glass-card rounded-2xl p-6 glow-gold">
          {success ? (
            <div className="text-center py-2">
              <motion.div
                initial={{ scale: 0.6, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: 'spring', stiffness: 260, damping: 18 }}
                className="mx-auto mb-4 h-14 w-14 rounded-full bg-sage/15 flex items-center justify-center"
              >
                <CheckCircle2 size={32} className="text-sage" />
              </motion.div>
              <h2 className="font-display text-xl text-ivory font-semibold mb-2">
                {t('passwordUpdated')}
              </h2>
              <p className="text-pearl/60 text-sm leading-relaxed mb-6">
                {t('passwordUpdatedDescription')}
              </p>
              <button
                type="button"
                onClick={clearPasswordSet}
                className="w-full bg-gold hover:bg-gold-light text-midnight font-semibold py-3 rounded-xl transition-colors"
              >
                {t('continueToApp')}
              </button>
            </div>
          ) : (
          <>
          <h2 className="font-display text-xl text-ivory font-semibold mb-2 text-center">
            {t('setPassword')}
          </h2>
          <p className="text-pearl/50 text-xs text-center mb-6">
            {t('setPasswordDescription')}
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-pearl/60 text-xs mb-1.5 ml-1">{t('newPasswordLabel')}</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  className="w-full bg-slate-dark/80 border border-gold/10 rounded-xl pl-4 pr-11 py-3 text-ivory text-sm placeholder:text-pearl/55 focus:outline-none focus:border-gold/40 transition-colors"
                  placeholder={t('passwordPlaceholder')}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? t('hidePassword') : t('showPassword')}
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-pearl/55 hover:text-ivory transition-colors"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-pearl/60 text-xs mb-1.5 ml-1">{t('confirmPasswordLabel')}</label>
              <input
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={6}
                className="w-full bg-slate-dark/80 border border-gold/10 rounded-xl px-4 py-3 text-ivory text-sm placeholder:text-pearl/55 focus:outline-none focus:border-gold/40 transition-colors"
                placeholder={t('confirmPasswordPlaceholder')}
              />
            </div>

            {error && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-rose text-xs text-center py-1"
              >
                {error}
              </motion.p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gold hover:bg-gold-light text-midnight font-semibold py-3 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading && <Loader2 size={16} className="animate-spin" />}
              {t('setPasswordButton')}
            </button>
          </form>
          </>
          )}
        </div>
      </motion.div>
    </div>
  )
}
